import { useState } from 'react';
import s from './AgencyProfileHeader.module.scss';
import { SkeletonRectangle } from '@wix/design-system';
import dynamic from 'next/dynamic';
import SkeletonLoggingInArea from './SkeletonLoginArea';
import { HeaderData } from '@components/Header/models';
import {
  dataHooks,
  EMPTY_HEADER_DATA,
} from '@components/Header/utils/constants';
import { addProtocolToPath } from '@components/Header/utils/addProtocolToPath';
import { useFallbackMediaQuery } from '@wix/experts-common';
import MobileMenuOpenButton from './MobileMenuOpenButton';
import FormattedMarketplaceLogo from '@components/Header/FormattedMarketplaceLogo';
import {
  IMPHeaderPageName,
  PAGE_NAMES,
} from '@components/Hooks/useMarketplaceHeaderBi';
import MobileScreens from './MobileScreens';
import { LogoTypes } from '@components/Header/FormattedMarketplaceLogo/FormattedMarketplaceLogo';

const DynamicLoggedinArea = dynamic(
  () =>
    import('@components/AgencyProfileHeader/LoginArea/LoggedInAccountDisplay'),
  {
    loading: () => <SkeletonRectangle height={'12px'} width={'72px'} />,
    ssr: false,
  },
);

const DynamicNotLoggedinArea = dynamic(
  () => import('@components/AgencyProfileHeader/LoginArea/LoginDisplay'),
  {
    loading: () => <SkeletonRectangle height={'12px'} width={'72px'} />,
    ssr: false,
  },
);

interface Props {
  isLoading?: boolean;
  onClick?: () => void;
  headerData?: HeaderData;
  language?: string;
  isInternal?: boolean;
  origin?: string;
  pageName?: string;
  isMobileUserAgent?: boolean;
}

const AgencyProfileHeader = ({
  isLoading,
  isInternal = false,
  headerData = EMPTY_HEADER_DATA,
  origin = 'https://www.wix.com',
  language = 'en',
  pageName = PAGE_NAMES.profile,
  isMobileUserAgent,
}: Props) => {
  const isLessThan1000View = useFallbackMediaQuery(
    '(max-width: 999px)',
    !!isMobileUserAgent,
  );

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  const marketplaceUrlPrefix = addProtocolToPath(`${origin}/marketplace`);
  const isUserLoggedIn = !!headerData?.userData?.email;
  const userProfileImg = headerData?.userProfileImage;

  return (
    <header data-hook={dataHooks.marketplaceHeader}>
      <div
        id="mph"
        className={`${s.marketplaceHeader} ${
          isMobileMenuOpen ? s.fixedTop : ''
        }`}
      >
        <div className={s.container}>
          <nav className={s.nav}>
            {isLoading && <SkeletonLoggingInArea />}
            <ul className={s.navList}>
              <FormattedMarketplaceLogo
                pageName={PAGE_NAMES.profile}
                isInternal={isInternal}
                logoType={LogoTypes.studio}
              />
              {!isUserLoggedIn && !isLoading ? (
                <DynamicNotLoggedinArea
                  language={language}
                  pageName={pageName}
                  isLessThan1000View={isLessThan1000View}
                />
              ) : (
                !isLoading && (
                  <li className={`${s.cell} ${s.desktop} ${s.right} ${s.last}`}>
                    <DynamicLoggedinArea
                      keys={headerData.keys}
                      userProfileImg={userProfileImg}
                      userData={headerData.userData}
                      pageName={pageName as IMPHeaderPageName}
                      isLessThan1000View={isLessThan1000View}
                      marketplaceUrlPrefix={marketplaceUrlPrefix}
                    />
                  </li>
                )
              )}
              <MobileMenuOpenButton
                isMobileMenuOpen={isMobileMenuOpen}
                setIsMobileMenuOpen={setIsMobileMenuOpen}
              />
            </ul>
          </nav>
        </div>
        <div
          role="list"
          data-hook="mobile-menu-container"
          className={`${s.mobileMenu} ${isMobileMenuOpen ? s.menuOpen : ''}`}
        >
          <MobileScreens
            isUserLoggedIn={isUserLoggedIn}
            marketplaceUrlPrefix={marketplaceUrlPrefix}
            language={language}
            headerData={headerData}
            pageName={pageName}
            isLess1000View={isLessThan1000View}
          />
        </div>
      </div>
    </header>
  );
};

export default AgencyProfileHeader;
